import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";

import * as echarts from "echarts";
import dayjs from "dayjs";

// 負責清除 cache tooltips
function hideOtherChartTooltips(currentChart) {
  const chartDoms = document.querySelectorAll(".echarts-container");
  chartDoms.forEach((dom) => {
    const chart = echarts.getInstanceByDom(dom);
    if (chart && chart !== currentChart) {
      chart.dispatchAction({ type: "hideTip" });
    }
  });
}

const ConnectBar = (props) => {
  const { info, joinedOn, startTime, endTime } = props;

  const theme = useTheme();
  const colors = {
    Online: theme.palette.primary.greenSecound,
    Offline: theme.palette.primary.redPrimary,
    Empty: theme.palette.primary.borderGreyPrimary,
  };
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  let datas = [];

  useEffect(() => {
    if (info === null || info.length === 0) return;

    datas = info.map((item) => ({
      itemStyle: { color: colors[item.name] },
      name: item.name,
      value: [0, item.value[0] * 1000, item.value[1] * 1000],
    }));

    if (datas.length <= 0) return;

    const chartDom = chartRef.current;
    if (!chartDom) return;

    const chart = echarts.init(chartDom, null, {
      renderer: "svg",
      useDirtyRect: true,
    });
    chartInstance.current = chart;

    // setting
    const option = {
      color: [
        theme.palette.primary.greenSecound,
        theme.palette.primary.redPrimary,
        theme.palette.primary.borderGreyPrimary,
      ],
      tooltip: {
        transitionDuration: 0,
        hideDelay: 0,
        triggerOn: "mousemove",
        appendToBody: false,
        position: function (point, params, dom, rect, size) {
          const xOffset = -100; // 向右偏移
          const yOffset = -80; // 向上偏移
          let x = point[0] + xOffset;
          let y = point[1] + yOffset;

          // 避免超出右邊界
          if (x + size.contentSize[0] > size.viewSize[0]) {
            x = point[0] - size.contentSize[0] - xOffset;
          }
          // 避免超出下邊界
          if (y + size.contentSize[1] > size.viewSize[1]) {
            y = point[1] - size.contentSize[1] - yOffset;
          }

          return [x, y];
        },
        backgroundColor: theme.palette.primary.greyPrimaryTransparency,
        textStyle: { color: theme.palette.primary.white },
        extraCssText: `border-radius: 5px; border:none;`,
        formatter: (params) => {
          if (!params) return "";

          // Join On 前 / 空白灰色區塊不顯示 tooltip
          if (params.name === "Empty") return "";

          return `<div style="font-size: 14px;">
            <span
              style="display:inline-block;width:8px;height:8px;
              background-color:${colors[params.name]};
              border-radius:50%;margin-right:6px;">
            </span>
            <span style="font-weight:bold;">${params.name}</span>
          </div>
          <div style="font-size: 14px;">
            <span>
              ${dayjs(params.value[1]).format("YYYY/MM/DD HH:mm")} - 
              ${dayjs(params.value[2]).format("YYYY/MM/DD HH:mm")}
            </span>
          </div>`;
        },
      },
      legend: {
        textStyle: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        },
        //data: ['Online', 'Offline', 'Empty'],
      },
      grid: {
        left: "8%",
        right: "5%",
        top: "15%",
        bottom: "10%",
        containLabel: false,
        borderWidth: 0,
      },
      xAxis: {
        axisLabel: {
          show: false,
          formatter: (val) => {
            return dayjs(val).format("HH:mm");
          },
        },
        axisLine: { show: false },
        splitLine: { show: false },
        type: "value",
        min: "dataMin",
        max: "dataMax",
      },
      yAxis: {
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        type: "category",
        // data: categories,  //['wan']
      },
      series: [
        // {name: states[0], type: 'bar', data:[],},
        // {name: states[1], type: 'bar', data:[],},
        // {name: states[2], type: 'bar', data:[],},
        {
          type: "custom",
          xAxisIndex: 0,
          yAxisIndex: 0,
          renderItem: renderItem,
          itemStyle: { opacity: 0.8 },
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: normalizeTimeData(datas),
        },
      ],
    };

    chartInstance.current.setOption(option);

    chart.on("showTip", () => {
      hideOtherChartTooltips(chart);
    });

    // resize
    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartDom);
    // clear
    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [info, startTime, endTime]);

  function normalizeTimeData(datas) {
    if (!Array.isArray(datas) || datas.length === 0) return [];

    const now = (endTime && !isNaN(Number(endTime))) ? Number(endTime) * 1000 : Date.now();
    const dayAgo = (startTime && !isNaN(Number(startTime))) ? Number(startTime) * 1000 : now - 24 * 60 * 60 * 1000;
    const joinedTime = joinedOn ? joinedOn * 1000 : null;

    const result = [];

    // Join On 前全部灰色
    if (joinedTime && joinedTime > dayAgo) {
      result.push({
        itemStyle: { color: colors.Empty },
        name: "Empty",
        value: [0, dayAgo, Math.min(joinedTime, now)],
      });
    }

    const actualStartTime = joinedTime && joinedTime > dayAgo ? joinedTime : dayAgo;

    const filtered = datas
      .map((item) => {
        let [row, start, end] = item.value;

        if (end < actualStartTime || start > now) return null;

        start = Math.max(start, actualStartTime);
        end = Math.min(end, now);

        const isValidStatus = item.name === "Online" || item.name === "Offline";

        return {
          ...item,
          name: isValidStatus ? item.name : "Empty",
          itemStyle: {
            color: isValidStatus ? colors[item.name] : colors.Empty,
          },
          value: [row, start, end],
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.value[1] - b.value[1]);

    let prevEnd = actualStartTime;

    filtered.forEach((item) => {
      const [row, start, end] = item.value;

      if (start > prevEnd) {
        result.push({
          itemStyle: { color: colors.Empty },
          name: "Empty",
          value: [row, prevEnd, start],
        });
      }

      result.push(item);
      prevEnd = Math.max(prevEnd, end);
    });

    if (prevEnd < now) {
      result.push({
        itemStyle: { color: colors.Empty },
        name: "Empty",
        value: [0, prevEnd, now],
      });
    }

    return result;
  }

  function renderItem(params, api) {
    const categoryIndex = api.value(0);
    const start = api.coord([api.value(1), categoryIndex]);
    const end = api.coord([api.value(2), categoryIndex]);
    const height = 20;
    const rectShape = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height / 2,
        width: end[0] - start[0],
        height: height,
      },
      {
        x: params.coordSys.x,
        y: params.coordSys.y,
        width: params.coordSys.width,
        height: params.coordSys.height,
      },
    );
    return {
      type: "rect",
      transition: ["shape"],
      shape: rectShape,
      style: {
        //api.style(),
        fill: api.visual("color"),
      },
    };
  }

  return (
    <>
      <div
        className="echarts-container"
        ref={chartRef}
        style={{
          width: "100%",
          height: "40px",
          //background: theme.palette.primary.white,
        }}
      />
    </>
  );
};

export default ConnectBar;
